import React, {useState, useEffect} from 'react';
import {ObjectId} from 'bson';
import {
  ScrollView,
  View,
  ImageBackground,
  TextInput,
  Text,
  Pressable,
  Image,
} from 'react-native';
import moment from 'moment';
import Icon from '../Shared/Icon';
import CustomPressable from '../Shared/CustomPressable';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {LabelValue} from '../../Types/DtoTypes';
import {Project} from '../../Models/PowerSyncModels/models';
import {ProjectSitesStyleSheet} from '../../Styles/ProjectSiteStyles';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import {Navigation} from '../../Constants/Navigation';
import LocalStorageService from '../../Services/LocalStorageService';
import {ILocalStorageService} from '../../Services/Interfaces/ILocalStorageService';
import {act} from 'react-dom/test-utils';
import {STORAGE_KEYS} from '../../Constants/AppConstants';
import {navigationRef, currentKey} from '../Shared/RootNavigation';
import {Formats} from '../../Constants/Formats';
import SearchBox from '../Shared/SearchBox';
import DynamicCheckBox from '../DynamicControls/DynamicCheckBox';
import Paginator from '../Shared/Paginator';

type ProjectSitesProps = {navigation: any};

const ProjectSites = (props: ProjectSitesProps): React.ReactElement => {
  const storageService: ILocalStorageService = new LocalStorageService();

  const [search, setSearch] = useState('');
  const [selectedSite, setSelectedSite] = useState<null | LabelValue<string>>(
    null,
  );

  const [activeSite, setActiveSite] = useState<null | LabelValue<string>>(null);

  const [projectCount, setProjectCount] = useState(0);
  const [projectDisplayCount, setProjectDisplayCount] = useState(5);
  const [projectSites, setProjectSites] = useState<Project[]>([]);
  const {projects} = usePowerSyncData();
  const [showInactive, setShowInactive] = useState(false);
  const [pageData, setPageData] = useState<Project[]>([]);
  const DISPLAY_INCREMENT = 5;

  useEffect(() => {
    const getSizeAsync = async () => {
      let count = projects.length;
      setProjectCount(count);
    };

    getSizeAsync();

    let activeSiteJSON = storageService.get(STORAGE_KEYS.ACTIVE_SITE_KEY);
    if (activeSiteJSON) {
      let parsedSite = JSON.parse(activeSiteJSON);
      let newActiveSite: LabelValue<string> = {
        label: parsedSite.label,
        value: parsedSite.value,
      };
      setActiveSite(newActiveSite);
    }
  }, []);

  useEffect(() => {
    if (navigationRef.current) {
      const unsubscribe = navigationRef.current.addListener('state', () => {
        fetchProjects();
      });

      return unsubscribe;
    }
  }, []);

  useEffect(() => {
    if (activeSite)
      storageService.set(
        STORAGE_KEYS.ACTIVE_SITE_KEY,
        JSON.stringify(activeSite),
      );
  }, [activeSite]);

  useEffect(() => {
    fetchProjects();
  }, [projectDisplayCount, search, activeSite, projects, showInactive]);

  const fetchProjects = async () => {
    let newProjects = projects.filter(
      x =>
        (x.name.toLowerCase().includes(search.toLowerCase()) || !search) &&
        !x.isDeleted,
    );

    let today = moment().startOf('day').toDate();
    newProjects = newProjects.filter(
      x =>
        showInactive ||
        ((!x.activeStartDate || x.activeStartDate <= today) &&
          (!x.activeEndDate || x.activeEndDate >= today)),
    );

    setProjectCount(newProjects.length);

    newProjects = newProjects.sort((a: Project, b: Project) => {
      return sortSites(a, b);
    });

    setProjectSites(newProjects);
  };

  const goToSite = (projectSiteId: string | null) => {
    props.navigation.push(Navigation.EDITPROJECTSITE, {
      projectSiteId: projectSiteId,
    });
  };

  //sort alphabetically with the active site on top
  const sortSites = (a: Project, b: Project) => {
    if (a.name === activeSite?.label) return -1;

    if (b.name === activeSite?.label) return 1;

    if (a.name!.toLocaleUpperCase() > b.name!.toLocaleUpperCase()) return 1;

    if (a.name!.toLocaleUpperCase() < b.name!.toLocaleUpperCase()) return -1;

    return 0;
  };

  const renderSiteItemActions = (site: LabelValue<string>) => {
    const actions = (
      <View style={ProjectSitesStyleSheet.actionContainer}>
        {site.value === activeSite?.value! ? (
          <CustomPressable
            onPress={() => {
              setActiveSite(null);
            }}
            style={({pressed}) => [
              pressed && {
                opacity: 0.6,
              },
              {
                padding: 0,
                marginRight: 5,
              },
            ]}
            tooltip='Remove as Default'>
            <Icon icon={'close'} color={Colors.teal} size={20} />
          </CustomPressable>
        ) : (
          <CustomPressable
            onPress={() => {
              setActiveSite(site);
            }}
            style={({pressed}) => [
              pressed && {
                opacity: 0.6,
              },
              {
                padding: 0,
                marginRight: 5,
              },
            ]}
            tooltip='Set as Default'>
            <Icon icon={'input-check'} color={Colors.teal} size={20} />
          </CustomPressable>
        )}
        <CustomPressable
          onPress={() => {
            goToSite(site.value!);
          }}
          style={({pressed}) => [
            pressed && {
              opacity: 0.6,
            },
            {
              padding: 0,
              marginRight: 5,
            },
          ]}
          tooltip='View / Edit'>
          <Icon icon={'eye'} color={Colors.teal} size={20} />
        </CustomPressable>
      </View>
    );

    return actions;
  };

  const renderProjectSites = () => {
    const today = moment().startOf('day').toDate();

    const filtered = projectSites.filter(
      x =>
        !x.isDeleted &&
        (!search || x.name.toLowerCase().includes(search.toLowerCase())) &&
        (showInactive ||
          ((!x.activeStartDate || x.activeStartDate <= today) &&
            (!x.activeEndDate || x.activeEndDate >= today))),
    );

    const sorted = filtered.sort((a, b) => sortSites(a, b));

    if (!filtered || filtered.length === 0) {
      return (
        <>
          <Text
            style={[
              ProjectSitesStyleSheet.siteItemText,
              {textAlign: 'center', marginTop: 24},
            ]}>
            No Results Found
          </Text>
        </>
      );
    }

    const paginator = (
      <Paginator
        data={sorted}
        pageSize={DISPLAY_INCREMENT}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        onPaginatedDataChange={setPageData}
        searchDeps={[search, showInactive, projectSites]}
      />
    );

    return (
      <>
        {pageData.map((x, i) => {
          const project: LabelValue<string> = {
            label:
              x.name +
              ((x.activeStartDate &&
                x.activeStartDate >= moment().startOf('day').toDate()) ||
              (x.activeEndDate &&
                x.activeEndDate <= moment().startOf('day').toDate())
                ? ' - Inactive'
                : ''),
            value: x._id,
          };

          const selected = selectedSite?.label === project.label;
          const active = activeSite?.label === project.label;

          return (
            <View
              key={'siteItem' + project.value}
              style={ProjectSitesStyleSheet.siteItem}>
              <Pressable
                onPress={() => setSelectedSite(project)}
                style={({pressed}) => [
                  ProjectSitesStyleSheet.siteItemPress,
                  {borderColor: Colors.teal, borderWidth: 2},
                  pressed && {backgroundColor: Colors.lightTealPressed},
                  active && {backgroundColor: Colors.teal},
                  active && pressed && {opacity: 0.6},
                ]}>
                <Text
                  style={[
                    ProjectSitesStyleSheet.siteItemText,
                    {color: active ? Colors.white : Colors.teal},
                  ]}
                  numberOfLines={1}>
                  {project.label}
                </Text>
                {activeSite?.value &&
                  project.value &&
                  activeSite.value === project.value && (
                    <View
                      style={{
                        backgroundColor: Colors.white,
                        borderRadius: 15,
                        padding: 2,
                      }}>
                      <Icon
                        icon={'input-check'}
                        color={Colors.teal}
                        size={15}
                      />
                    </View>
                  )}
              </Pressable>
              {selected && renderSiteItemActions(project)}
            </View>
          );
        })}
        <View style={{marginTop: 24}}>{paginator}</View>
      </>
    );
  };

  return (
    <ScrollView style={{backgroundColor: 'white'}}>
      <Pressable
        style={{
          //@ts-ignore
          cursor: 'auto',
        }}
        onPress={() => {
          setSelectedSite(null);
        }}>
        <>
          <View>
            <ImageBackground
              source={Images.PEOPLE}
              resizeMode='cover'
              style={{height: 260}}>
              <Text style={ReportsStyleSheet.headerTitle}>Projects/Sites</Text>
            </ImageBackground>
          </View>
          <View style={{flexDirection: 'row'}}>
            <View style={{flex: 2, padding: 28}}>
              <View
                style={{marginTop: -58, marginLeft: 'auto', marginRight: 28}}>
                <CustomPressable
                  style={({pressed}) => [
                    {
                      backgroundColor: Colors.green,
                      width: 58,
                      height: 58,
                      borderRadius: 58,
                    },
                    pressed && {
                      backgroundColor: Colors.teal,
                    },
                  ]}
                  onPress={() => goToSite(null)}
                  tooltip='Add New'>
                  <View style={{marginTop: -1, marginLeft: -1}}>
                    <Icon icon={'add'} color={Colors.white} size={60} />
                  </View>
                </CustomPressable>
              </View>
              <SearchBox onChangeSearch={newSearch => setSearch(newSearch)} />
              <View
                style={{
                  alignSelf: 'flex-end',
                  marginHorizontal: 20,
                  marginTop: 6,
                }}>
                <DynamicCheckBox
                  label='Show Inactive'
                  value={showInactive ? 'true' : 'false'}
                  onChange={(controlId, controlTypeId, value) =>
                    setShowInactive(value === 'true')
                  }
                />
              </View>
              <View style={ProjectSitesStyleSheet.siteList}>
                {renderProjectSites()}
              </View>
            </View>
            <View style={{flex: 1}}>
              <View style={CommonStyleSheet.bcsfFooter}>
                <Image
                  source={Images.BCSF}
                  style={CommonStyleSheet.bcsfFooterImage}
                />
                <Text style={CommonStyleSheet.bcsfFooterText}>
                  BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
                </Text>
              </View>
            </View>
          </View>
        </>
      </Pressable>
    </ScrollView>
  );
};

export default ProjectSites;
